import React, { useMemo, useState } from 'react';
import { Button, Divider, Flex, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useIntl, useSelector } from 'umi';

import { useHasAccess } from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import { selectExtraFieldTypesList } from '@/selectors/extraFieldsSelectors';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { ICON_MAP } from './ExtraFieldsList';
import ExtraFieldDrawer from './ExtraFieldDrawer';
import styles from './ExtraFieldSelector.less';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';

interface ExtraFieldSelectorProps {
  /**
   * Context for filtering fields (e.g., 'customer', 'result', 'serviceRequest')
   */
  relatedTo: string;
  /**
   * List of already selected field UUIDs to prevent duplicates
   */
  selectedFieldIds?: string[];
  /**
   * Callback when a field is selected
   */
  onFieldSelect: (field: ExtraFieldTypeModel) => void;
  /**
   * Optional placeholder text
   */
  placeholder?: string;
  /**
   * Callback when a new field is created via the drawer
   */
  onFieldCreated?: (field: ExtraFieldTypeModel) => void;
}



/**
 * ExtraFieldSelector - A reusable component for selecting and creating extra fields
 *
 * Features:
 * - Select dropdown with search
 * - Field list filtered by related_to context
 * - Already-added fields are disabled
 * - Create new field functionality in dropdown footer
 */
const ExtraFieldSelector: React.FC<ExtraFieldSelectorProps> = ({
                                                                 relatedTo,
                                                                 selectedFieldIds = [],
                                                                 onFieldSelect,
                                                                 onFieldCreated,
                                                               }) => {
  const intl = useIntl();
  const [selectOpen, setSelectOpen] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [newFieldTemplate, setNewFieldTemplate] = useState<ExtraFieldTypeModel | null>(null);
  const hasExtraFieldAccess = useHasAccess([
    PERMISSIONS.ExtraFieldType.CREATE,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);

  // Get all extra fields from redux
  const allExtraFields = useSelector(selectExtraFieldTypesList);

  // Filter fields by related_to
  const filteredFields = useMemo(() => {
    return allExtraFields.filter((field) => field.related_to === relatedTo);
  }, [allExtraFields, relatedTo]);

  const handleFieldSelect = (uuids: string[]) => {
    const uuid = uuids?.[0];
    const field = filteredFields.find((f) => f.uuid === uuid);
    if (field) {
      onFieldSelect(field);
    }
    setTimeout(() => setSelectOpen(true), 0);
  };

  const handleCreateNewField = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Create a template for new field with pre-filled related_to
    setNewFieldTemplate({
      uuid: '',
      label: '',
      slug: '',
      description: '',
      field_type: 'text',
      related_to: relatedTo,
      is_editable: true,
      is_auto_attached: false,
      options: [],
    });
    setCreateDrawerOpen(true);
    setSelectOpen(false);
  };

  const handleDrawerClose = () => {
    setCreateDrawerOpen(false);
    setNewFieldTemplate(null);
  };

  const FieldIcon = ({ fieldType }: { fieldType: string }) => {
    const IconComponent = ICON_MAP[fieldType];
    if (!IconComponent) return null;

    return (
      <IconComponent style={{ width: '16px' }} />
    );
  };

  return (
    <>
      <Select
        showSearch
        placeholder={
          <Space>
            <PlusOutlined />
            <F entity={INTL_ENTITY.EXTRA_FIELD} action={INTL_ACTION.ADD} />
          </Space>
        }
        bordered={false}
        className={styles.buttonSelect}
        open={selectOpen}
        onOpenChange={setSelectOpen}
        onChange={handleFieldSelect}
        showArrow={false}
        value={null}
        mode={"multiple"}
        optionFilterProp="label"
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        dropdownRender={(menu) => (
          <>
            {menu}
            {hasExtraFieldAccess && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <div onMouseDown={(e) => e.preventDefault()}>
              <Button
                size={'small'}
                type="link"
                icon={<PlusOutlined />}
                onClick={handleCreateNewField}
                className={styles.createButton}
              >
                <F entity={INTL_ENTITY.EXTRA_FIELD} action={INTL_ACTION.NEW} />
              </Button>
            </div>
            </>
            )}
            </>
        )}
        options={filteredFields.map((field) => ({
          value: field.uuid,
          label: field.label,
          disabled: selectedFieldIds.includes(field.uuid),
          field: field,
        }))}
        optionRender={(option) => {
          const field = filteredFields.find((f) => f.uuid === option.value);
          if (!field) return option.label;

          return (
            <Flex align={'center'} gap={6}>
              <FieldIcon fieldType={field.field_type} />
              {field.label}
            </Flex>
          );
        }}
      />

      {newFieldTemplate && (
        <ExtraFieldDrawer
          editingField={newFieldTemplate}
          onClose={handleDrawerClose}
          setEditingField={setNewFieldTemplate}
          isNew={true}
          onFieldCreated={onFieldCreated}
        />
      )}
    </>
  );
};

export default ExtraFieldSelector;
